/* original source: https://ui.shadcn.com/docs/components/collapsible */

'use client'
import { Button, RotatingChevron } from './Button'
import type { ButtonProps } from './Button'
import { tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import { Collapsible as CollapsiblePrimitive } from 'radix-ui'
import type { ComponentProps, ReactNode } from 'react'

export type CollapsibleProps = Omit<
  ComponentProps<typeof CollapsiblePrimitive.Root>,
  'className'
>

export function Collapsible(props: CollapsibleProps) {
  return <CollapsiblePrimitive.Root data-slot="collapsible" {...props} />
}

export type CollapsibleTriggerProps = Omit<
  ComponentProps<typeof CollapsiblePrimitive.CollapsibleTrigger>,
  'className'
>

export function CollapsibleTrigger(props: CollapsibleTriggerProps) {
  return (
    <CollapsiblePrimitive.CollapsibleTrigger
      data-slot="collapsible-trigger"
      {...props}
    />
  )
}

export const collapsibleContentVariants = tv({
  base: 'min-w-0 overflow-hidden',

  defaultVariants: {
    animationVariant: 'default',
  },

  variants: {
    animationVariant: {
      default:
        'motion-safe:data-[state=closed]:animate-collapse-up motion-safe:data-[state=open]:animate-collapse-down',
      none: null,
      slow: 'motion-safe:data-[state=closed]:animate-collapse-up-slow motion-safe:data-[state=open]:animate-collapse-down-slow',
    },
  },
})

export interface CollapsibleContentProps
  extends
    Omit<
      ComponentProps<typeof CollapsiblePrimitive.CollapsibleContent>,
      'className'
    >,
    VariantProps<typeof collapsibleContentVariants> {}

export function CollapsibleContent({
  animationVariant,
  ...props
}: CollapsibleContentProps) {
  return (
    <CollapsiblePrimitive.CollapsibleContent
      className={collapsibleContentVariants({ animationVariant })}
      data-slot="collapsible-content"
      {...props}
    />
  )
}

export interface CollapsibleButtonProps extends ButtonProps {
  children: ReactNode
}

export function CollapsibleButton(props: CollapsibleButtonProps) {
  return (
    <div className="self-start">
      <Button
        size="sm"
        variant="ghost"
        colorVariant="emphasis"
        endIcon={
          <RotatingChevron
            className="size-2.5 fill-current stroke-2"
            orientation="horizontal"
          />
        }
        {...props}
      />
    </div>
  )
}
